import { useMemo } from 'react'

import { PrototypeConfig } from '@sketch-hq/sketch-prototype-player'
import { useInitialPrototypeArtboardId } from './usePrototypeViewContext/useInitialPrototypeArtboardId'
import { useLocation } from 'react-router-dom'
import { parse } from 'query-string'
import { usePrototypeViewContext } from './usePrototypeViewContext'
import { darkTheme } from '@sketch/global-styles'

type UsePrototypeConfigProps = {
  resolveImageSrc: ((path: string) => string) | null
}

/**
 * Returns a memoized prototype config object.
 */
export function usePrototypeConfig(
  props: UsePrototypeConfigProps
): PrototypeConfig {
  const { resolveImageSrc } = props
  const initialArtboardId = useInitialPrototypeArtboardId()
  const showHotzones = useShowHotzones()
  const { onUnhandledClickInteraction } = usePrototypeViewContext()

  return useMemo(
    () => ({
      allowRevealHotspots: showHotzones,
      prototypeBackgroundColor: darkTheme.colors.foreground.primary.A,
      initialArtboardId,
      resolveImageSrc: resolveImageSrc || ((path: string) => path),
      onUnhandledClickInteraction,
    }),
    [
      initialArtboardId,
      showHotzones,
      resolveImageSrc,
      onUnhandledClickInteraction,
    ]
  )
}

function useShowHotzones() {
  const { search } = useLocation()

  return useMemo(() => getShowHotzones(search), [search])
}

const getShowHotzones = (queryString: string) => {
  let { hotspots } = parse(queryString)
  if (!hotspots) {
    return true
  }

  // We are expecting only one query param,
  // but just in case we have multiple, take the first one.
  if (Array.isArray(hotspots)) {
    hotspots = hotspots[0]
  }

  const shouldHideHotspots =
    hotspots!.toLowerCase() === 'false' || hotspots === '0'

  return !shouldHideHotspots
}
